import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AnalyticsCupNav } from './components/AnalyticsCupNav';
import './AnalyticsCup.scss';

import edition1 from '../../assets/analytics_cup/editions/analytics-cup.json';
import edition2 from '../../assets/analytics_cup/editions/analytics-cup2.json';
import submissionsCSV from '../../assets/analytics_cup/analytics-cup-26.csv';
import { findVideo, youtubeUrl } from '../../assets/analytics_cup/videoLookup';

// ── Editions registry ─────────────────────────────────────────────────────────

const EDITIONS = [
    { ...edition2, status: 'open',      image: '/images/optimized/hero-paris-boston_800.webp' },
    { ...edition1, status: 'completed', image: '/images/optimized/pysport_paris.jpg' },
];

// ── CSV helpers ───────────────────────────────────────────────────────────────

const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { inQuotes = !inQuotes; }
        else if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
        else { current += ch; }
    }
    result.push(current.trim());
    return result;
};

const parseCSV = (text) =>
    text.trim().split('\n').slice(1).map(line => {
        const v = parseCSVLine(line);
        return {
            id: v[0], title: v[1], track: v[2], name: v[3],
            finalist: v[4] === 'TRUE',
            honourableMention: v[5] === 'TRUE',
            github: v[7],
        };
    });

const capitalizeName = (name = '') =>
    name.toLowerCase().split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ').replace(/_/g, ' ');

// ── 2026 Winner block ─────────────────────────────────────────────────────────

const WinnerBlock = () => {
    const [finalists, setFinalists] = useState([]);

    useEffect(() => {
        fetch(submissionsCSV)
            .then(r => r.text())
            .then(text => setFinalists(parseCSV(text).filter(s => s.finalist)))
            .catch(() => {});
    }, []);

    return (
        <section id="winner" className="ac-main__winner">
            <div className="container">
                <h2 className="ac-main__winner-title">Winner</h2>

                <div className="ac-main__winner-layout">

                    {/* Left: video */}
                    <div className="ac-main__winner-video">
                        <iframe
                            src="https://www.youtube.com/embed/UWhVXbS69BE"
                            title="Analytics Cup 2026 — Amar Shah finalist presentation"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>

                    {/* Right: winner profile */}
                    <div className="ac-main__winner-profile">
                        <div className="ac-main__winner-identity">
                            <img src="/images/optimized/winner.png" className="ac-main__winner-headshot" alt="Amar Shah" />
                            <div>
                                <p className="ac-main__winner-name">Amar Shah</p>
                                <p className="ac-main__winner-project">
                                    <a
                                        href="https://amarshah.dev/blog/simulated-annealing-positional-optimization/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Simulated Annealing for Positional Optimisation
                                    </a>
                                </p>
                            </div>
                        </div>

                        <p className="ac-main__winner-body">
                            Amar's winning project was subsequently developed into a module within
                            the DataballPy Python package, making it available for the wider
                            analytics community to use and build upon.
                        </p>

                        <div className="ac-main__winner-ctas">
                            <Link
                                to="/analytics-cup/videos"
                                className="ac-main__winner-cta ac-main__winner-cta--primary"
                            >
                                Watch the 2026 finalist presentations
                            </Link>
                            <Link
                                to="/analytics-cup/editions/analytics-cup"
                                className="ac-main__winner-cta ac-main__winner-cta--secondary"
                            >
                                View Edition 1.0
                            </Link>
                        </div>
                    </div>

                    {/* Full-width row: Edition 1.0 finalists */}
                    {finalists.length > 0 && (
                        <div className="ac-main__winner-finalists">
                            <div className="ac-main__winner-finalists-grid">
                                {finalists.map(s => {
                                    const trackSlug = s.track === 'Research Track' ? 'research' : 'analyst';
                                    return (
                                        <div
                                            className={`ac-submission-card ac-submission-card--${trackSlug}`}
                                            key={s.id}
                                        >
                                            <div className="ac-submission-card__badges">
                                                <span className="ac-submission-card__track">{s.track}</span>
                                                <span className="ac-submission-card__badge ac-submission-card__badge--finalist">
                                                    Finalist
                                                </span>
                                            </div>
                                            <h4 className="ac-submission-card__name">{capitalizeName(s.name)}</h4>
                                            <p className="ac-submission-card__title">
                                                <a href={s.github} target="_blank" rel="noopener noreferrer">
                                                    {s.title}
                                                </a>
                                            </p>
                                            {(() => { const v = findVideo(s.name); return v ? (
                                                <a href={youtubeUrl(v.youtubeId)} target="_blank" rel="noopener noreferrer" className="ac-submission-card__video-link">
                                                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/></svg>
                                                    Watch presentation
                                                </a>
                                            ) : null; })()}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="ac-main__winner-finalists-cta">
                                <Link
                                    to="/analytics-cup/submissions"
                                    className="ac-main__winner-cta ac-main__winner-cta--secondary"
                                >
                                    All Submissions
                                </Link>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </section>
    );
};

// ── Take part block ───────────────────────────────────────────────────────────

const TakePartBlock = () => (
    <section id="take-part" className="ac-main__takepart" style={{ background: "linear-gradient(135deg, rgba(14,31,48,0.22) 0%, rgba(12,40,32,0.92) 100%), url('/images/optimized/hero-paris-boston-green_1920.webp') center/cover no-repeat" }}>
        <div className="container">
            <div className="ac-main__takepart-inner">
                <div className="ac-main__takepart-heading-wrap">
                    <h2 className="ac-main__takepart-heading">
                        Take part in<br />Analytics Cup 2.0
                    </h2>
                </div>
                <div className="ac-main__takepart-body">
                    <p>
                        Analytics Cup 2.0 introduces two sports, two regional competitions
                        and two live finals.
                    </p>
                    <p>
                        Participants can choose between Football and Basketball data and enter
                        either the Europe competition, culminating in Paris, or the USA
                        competition, culminating in Boston.
                    </p>
                    <p>
                        Whether you are a student, independent analyst, researcher, developer
                        or established industry professional, the Analytics Cup is an
                        opportunity to work with SkillCorner data, explore a question that
                        matters and share your ideas with the global sports analytics community.
                    </p>
                    <Link
                        to="/analytics-cup/editions/analytics-cup2"
                        className="ac-main__takepart-cta"
                    >
                        Discover Analytics Cup 2.0
                    </Link>
                </div>
            </div>
        </div>
    </section>
);

// ── Edition cards ─────────────────────────────────────────────────────────────

const EditionCard = ({ edition }) => (
    <Link
        to={`/analytics-cup/editions/${edition.slug}`}
        className={`ac-main__edition-card ac-main__edition-card--${edition.status}`}
    >
        <div className="ac-main__edition-card-image">
            <img src={edition.image} alt={edition.name} />
            <span className={`ac-main__edition-status ac-main__edition-status--${edition.status}`}>
                {edition.status === 'completed' ? 'Completed' : 'Open'}
            </span>
        </div>
        <div className="ac-main__edition-card-body">
            <h3 className="ac-main__edition-name">{edition.name}</h3>
            <p className="ac-main__edition-tagline">{edition.tagline}</p>
            <span className="ac-main__edition-link">View edition →</span>
        </div>
    </Link>
);

// ── Page ──────────────────────────────────────────────────────────────────────

export const AnalyticsCupMain = () => (
    <div className="analytics-cup analytics-cup--landing">
        <Helmet>
            <title>Analytics Cup | PySport</title>
            <meta name="description" content="The Analytics Cup is a global sports analytics challenge by SkillCorner and PySport. Explore Football and Basketball data, compete in Europe or the USA, and turn your ideas into open-source tools." />
        </Helmet>
        <AnalyticsCupNav />

        {/* Hero */}
        <section
            className="hero-banner"
            aria-label="Analytics Cup hero"
            style={{ backgroundImage: "url(/images/optimized/banner_talk_1920.webp)" }}
        >
            <div className="hero-banner-content">
                    <div className="container">
                        <div className="ac-main__hero">
<h1 className="ac-main__hero-heading">
                                Opening Sports Data<br />To New Ideas
                            </h1>
                            <p className="ac-main__hero-body">
                                Advanced tracking and performance data has traditionally been
                                available only to a relatively small group of professional teams
                                and organisations. We created the Analytics Cup with{' '}
                                <span className="ac-main__highlight">PySport</span> to
                                open <span className="ac-main__highlight--alt">SkillCorner</span> data,
                                explore a question that matters and share
                                your ideas with the global sports analytics community.
                            </p>
                            <Link
                                to="/analytics-cup/editions/analytics-cup2"
                                className="ac-main__hero-cta"
                            >
                                Discover Analytics Cup 2.0
                            </Link>
                            <div className="ac-main__hero-partners">
                                <span className="ac-main__hero-partners-label">In partnership with</span>
                                <div className="ac-main__hero-partners-logos">
                                    <img src="/images/skillcorner-logo-white.svg" alt="SkillCorner" />
                                    <img src="/images/icons/pysport_logo_wordmark_white.svg" alt="PySport" />
                                </div>
                            </div>
                        </div>
                    </div>
            </div>
        </section>

        {/* About */}
        <section id="about" className="ac-main__about">
            <div className="container">
                <div className="ac-main__about-grid">
                    <div className="ac-main__about-col">
                        <div className="ac-main__about-quote">
                            <h2 className="ac-main__about-heading">
                                <span className="ac-main__highlight--alt">SkillCorner</span> and{' '}
                                <span className="ac-main__highlight">PySport</span> share a belief
                                in the value of open data, collaboration and creating more
                                opportunities for people to contribute to sports analytics.
                            </h2>
                        </div>
                    </div>
                    <div className="ac-main__about-col">
                        <p>
                            <span className="ac-main__highlight--alt">SkillCorner</span> provides
                            the tracking data, technical documentation and industry context needed
                            to explore meaningful questions across professional sport.{' '}
                            <span className="ac-main__highlight">PySport</span> brings together a
                            global community of analysts, researchers and open-source developers,
                            with the expertise to help participants turn ideas into accessible and
                            reusable work.
                        </p>
                        <p>Together, we created the Analytics Cup to:</p>
                        <ul className="ac-main__about-list">
                            <li>Make advanced sports data more accessible to the wider analytics community</li>
                            <li>Encourage new applications of tracking and performance data</li>
                            <li>Support open-source tools, research and shared learning</li>
                            <li>Connect emerging talent with leading clubs and industry professionals</li>
                            <li>Give successful participants a platform to showcase their work and capabilities</li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>

        {/* 2026 Winner */}
        <WinnerBlock />

        {/* Take part in Analytics Cup 2.0 */}
        <TakePartBlock />

        {/* Editions */}
        <section id="editions" className="ac-main__editions">
            <div className="container">
                <h2 className="ac-main__section-title">Editions</h2>
                <div className="ac-main__editions-grid">
                    {EDITIONS.map(ed => (
                        <EditionCard key={ed.slug} edition={ed} />
                    ))}
                </div>
            </div>
        </section>

        {/* Contact */}
        <section id="contact" className="ac-main__contact">
            <div className="container">
                <div className="ac-main__contact-header">
                    <h2 className="ac-main__contact-heading">
                        Join the community
                    </h2>
                    <p className="ac-main__contact-sub">
                        Analytics Cup is built on the collaboration between SkillCorner and PySport.
                        Follow the people behind it to stay up to date.
                    </p>
                    {/* Discord link hidden until we have the invite URL
                    <a
                        href=""
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ac-main__contact-discord"
                    >
                        Join our Discord
                    </a>
                    */}
                </div>

                <div className="ac-main__contact-grid">

                    <div className="ac-main__contact-card ac-main__contact-card--pysport">
                        <div className="ac-main__contact-card-logo">
                            <img src="/images/icons/pysport_logo_wordmark_white.svg" alt="PySport" />
                        </div>
                        <p className="ac-main__contact-card-body">
                            A global community of analysts, researchers and open-source developers.
                            Join the conversation on Discord or explore the code on GitHub.
                        </p>
                        <div className="ac-main__contact-card-ctas">
                            <a
                                href="https://pysport.org"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ac-main__contact-cta ac-main__contact-cta--pysport"
                            >
                                PySport.org
                            </a>
                            <a
                                href="https://github.com/PySport"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ac-main__contact-cta ac-main__contact-cta--outline-pysport"
                            >
                                GitHub
                            </a>
                        </div>
                    </div>

                    <div className="ac-main__contact-card ac-main__contact-card--skillcorner">
                        <div className="ac-main__contact-card-logo">
                            <img src="/images/skillcorner-logo-white.svg" alt="SkillCorner" />
                        </div>
                        <p className="ac-main__contact-card-body">
                            Providing the tracking data, technical documentation and industry
                            context that powers the Analytics Cup. Visit SkillCorner to learn more.
                        </p>
                        <div className="ac-main__contact-card-ctas">
                            <a
                                href="https://skillcorner.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ac-main__contact-cta ac-main__contact-cta--skillcorner"
                            >
                                SkillCorner.com
                            </a>
                            <a
                                href="https://github.com/SkillCorner"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ac-main__contact-cta ac-main__contact-cta--outline-skillcorner"
                            >
                                GitHub
                            </a>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    </div>
);
